import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import { devLogSafe, devLog, devError } from './utils/secureLogger';
import { nacmeLogo } from './assets/images';
import { ResponsiveTable, MobileMultiCompanyStudentCard, MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminMultiCompanyStudents = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState('');

  useEffect(() => {
    fetchMultiCompanyStudents();
  }, []);

  // Filter students based on search term, status, and eligibility
  useEffect(() => {
    let filtered = students;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(studentData => {
        const student = studentData.student_info;
        const searchLower = searchTerm.toLowerCase();
        return (
          student.name.toLowerCase().includes(searchLower) ||
          student.email.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(studentData => 
        studentData.student_info.verification_status === statusFilter
      );
    }

    // Apply eligibility filter
    if (eligibilityFilter) {
      const isEligible = eligibilityFilter === 'eligible';
      filtered = filtered.filter(studentData => {
        const eligibility = studentData.student_info.eligibility_status;
        return isEligible ? (eligibility === true) : (eligibility === false || eligibility === null);
      });
    }

    setFilteredStudents(filtered);
  }, [students, searchTerm, statusFilter, eligibilityFilter]);

  const fetchMultiCompanyStudents = async (withDebug = false) => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Your session has expired. Please log in again.');
        setLoading(false);
        setTimeout(() => {
          navigate('/');
        }, 2000);
        return;
      }

      const url = withDebug 
        ? `${BACKEND_URL}/api/multi-company-students?debug=true`
        : `${BACKEND_URL}/api/multi-company-students`;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const studentsData = response.data.students || [];
      
      // Debug: Log eligibility values for first few students
      if (withDebug && studentsData.length > 0) {
        console.log('DEBUG: First few students eligibility values:');
        studentsData.slice(0, 5).forEach((studentData, i) => {
          console.log(`${i+1}. ${studentData.student_info.name}: eligibility_status = ${JSON.stringify(studentData.student_info.eligibility_status)} (type: ${typeof studentData.student_info.eligibility_status})`);
        });
      }
      
      setStudents(studentsData);
      setFilteredStudents(studentsData);
      setDebugInfo(response.data.debug || null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching multi-company students:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem('token');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch multi-company students. Please ensure you are logged in as an admin.');
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  // Format companies list for display
  const formatCompanies = (organizations) => {
    return organizations.map(org => org.organization).join(', ');
  };

  // Format verification status for display
  const formatStatus = (status) => {
    return status === 'verified' ? 'Verified' : 'Pending';
  };

  // Format eligibility status for display
  const formatEligibility = (eligibility) => {
    if (eligibility === true || eligibility === 'true') return 'Eligible';
    if (eligibility === false || eligibility === 'false') return 'Not Eligible';
    return 'Unknown';
  };

  // CSV Export functionality
  const exportToCSV = () => {
    if (filteredStudents.length === 0) {
      alert('No multi-company students to export.');
      return;
    }

    // CSV headers
    const headers = [
      'Student Name',
      'Number of Matches',
      'Companies',
      'Status',
      'Eligibility',
      'Email'
    ];

    // Convert students to CSV rows
    const csvRows = [
      headers.join(','), // Header row
      ...filteredStudents.map(studentData => [
        `"${studentData.student_info.name}"`,
        studentData.match_count,
        `"${formatCompanies(studentData.matching_organizations)}"`,
        studentData.student_info.verification_status === 'verified' ? 'Verified' : 'Pending',
        studentData.student_info.eligibility_status !== null ? (studentData.student_info.eligibility_status === true ? 'Eligible' : 'Not Eligible') : '-',
        studentData.student_info.email
      ].join(','))
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `Multi_Company_Students_${timestamp}.csv`);
      
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-100 text-red-700 p-4 rounded-md">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="multi-company"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'students') {
            // Students button handled by grid menu
          } else if (tabId === 'recruiters') {
            // Recruiters button handled by grid menu
          } else if (tabId === 'profile') {
            // Logout functionality
            localStorage.removeItem('token');
            navigate('/admin');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4">
            {/* Logo and Title Row */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <img
                src={nacmeLogo}
                alt="NACME Logo"
                className="h-10 sm:h-12 w-auto drop-shadow"
              />
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                Multi-Company Students
              </h1>
            </div>
            
            {/* Action Buttons Row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:justify-end">
              <button
                onClick={() => navigate('/admin/organization-matches')}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-center text-sm sm:text-base"
              >
                <span className="hidden sm:inline">Back to Organization Matches</span>
                <span className="sm:hidden">Back to Matches</span>
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center text-sm sm:text-base"
              >
                <span className="hidden sm:inline">Back to Admin Panel</span>
                <span className="sm:hidden">Back to Admin</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                {filteredStudents.length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {searchTerm || statusFilter || eligibilityFilter ? 'Filtered' : 'Total'} Students
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
                {students.length}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Multi-Company Students
              </div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                {students.length > 0 ? Math.round(students.reduce((sum, student) => sum + student.match_count, 0) / students.length * 10) / 10 : 0}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Average Matches per Student
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Search Bar */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Students
              </label>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
              >
                <option value="">All Statuses</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Eligibility Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Eligibility
              </label>
              <select
                value={eligibilityFilter}
                onChange={(e) => setEligibilityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-sm sm:text-base"
              >
                <option value="">All Eligibility</option>
                <option value="eligible">Eligible</option>
                <option value="not_eligible">Not Eligible</option>
              </select>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-6 grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4">
          <button
            onClick={() => fetchMultiCompanyStudents()}
            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
          >
            <span className="hidden sm:inline">Refresh Data</span>
            <span className="sm:hidden">Refresh</span>
          </button>
          <button
            onClick={exportToCSV}
            className="col-span-2 sm:col-span-1 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <svg className="w-3 sm:w-4 h-3 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">Export</span>
          </button>
          <button
            onClick={() => {
              setDebugMode(!debugMode);
              fetchMultiCompanyStudents(!debugMode);
            }}
            className={`col-span-2 sm:col-span-1 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${
              debugMode 
                ? 'bg-orange-600 text-white hover:bg-orange-700' 
                : 'bg-gray-400 text-white hover:bg-gray-500'
            }`}
          >
            {debugMode ? 'Hide Debug' : 'Show Debug'}
          </button>
        </div>

        {/* Debug Information */}
        {debugMode && debugInfo && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
              Debug Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Total Students: {debugInfo.total_students}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Total Recruiters: {debugInfo.total_recruiters}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Students Processed: {debugInfo.students_processed}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Multi-Company Students: {debugInfo.multi_company_students}
                </p>
              </div>
            </div>
            
            {/* Additional Debug Info */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Students with 0 matches: {debugInfo.students_with_0_matches || 'N/A'}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Students with 1 match: {debugInfo.students_with_1_match || 'N/A'}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Students with 2+ matches: {debugInfo.students_with_multiple_matches || 'N/A'}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-700 dark:text-yellow-300">
                  Final count returned: {debugInfo.final_students_count || 'N/A'}
                </p>
              </div>
            </div>
            
            {/* Error tracking */}
            {(debugInfo.students_with_errors || debugInfo.attribute_errors || debugInfo.other_errors) && (
              <div className="mb-4 space-y-1">
                {debugInfo.students_with_errors && (
                  <p className="font-medium text-red-700 dark:text-red-300">
                    Students with processing errors: {debugInfo.students_with_errors}
                  </p>
                )}
                {debugInfo.attribute_errors && (
                  <p className="font-medium text-red-700 dark:text-red-300">
                    Attribute errors when building result: {debugInfo.attribute_errors}
                  </p>
                )}
                {debugInfo.other_errors && (
                  <p className="font-medium text-red-700 dark:text-red-300">
                    Other errors when building result: {debugInfo.other_errors}
                  </p>
                )}
              </div>
            )}

            {/* Sample Student Matches */}
            {debugInfo.sample_student_matches && debugInfo.sample_student_matches.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-yellow-700 dark:text-yellow-300 mb-2">Sample Students with Matches:</h4>
                <div className="text-xs space-y-1">
                  {debugInfo.sample_student_matches.map((student, idx) => (
                    <div key={idx} className="bg-yellow-100 dark:bg-yellow-800 p-2 rounded">
                      <strong>{student.name}</strong> ({student.match_count} matches): {student.organizations.join(', ')}
                      <br />
                      <span className="text-yellow-600 dark:text-yellow-400">
                        GPA: {student.gpa}, Major: {student.major}, University: {student.university}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sample Non-Matching Students */}
            {debugInfo.sample_non_matches && debugInfo.sample_non_matches.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-yellow-700 dark:text-yellow-300 mb-2">Sample Students with No Matches:</h4>
                <div className="text-xs space-y-1">
                  {debugInfo.sample_non_matches.map((student, idx) => (
                    <div key={idx} className="bg-red-100 dark:bg-red-800 p-2 rounded">
                      <strong>{student.name}</strong>: GPA: {student.gpa}, Major: {student.major}, University: {student.university}, Class: {student.class_year}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sample Recruiters */}
            {debugInfo.recruiter_sample && debugInfo.recruiter_sample.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-yellow-700 dark:text-yellow-300 mb-2">Sample Recruiter Requirements:</h4>
                <div className="text-xs space-y-1">
                  {debugInfo.recruiter_sample.map((recruiter, idx) => (
                    <div key={idx} className="bg-blue-100 dark:bg-blue-800 p-2 rounded">
                      <strong>{recruiter.organization}</strong> - Min GPA: {recruiter.minimum_gpa}
                      <br />
                      <span className="text-blue-600 dark:text-blue-400">
                        Majors: {recruiter.eligible_majors}<br />
                        Universities: {recruiter.preferred_institutions}<br />
                        Class Years: {recruiter.preferred_class_years}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Students Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white">
                Students with Multiple Company Matches
              </h2>
              <span className="text-sm text-gray-600 dark:text-gray-400 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full w-fit">
                {filteredStudents.length} total
              </span>
            </div>
            
            {filteredStudents.length > 0 ? (
              <ResponsiveTable 
                data={filteredStudents}
                renderMobileCard={(studentData) => (
                  <MobileMultiCompanyStudentCard
                    key={`${studentData.student_info.name}-${studentData.match_count}`}
                    studentData={studentData}
                    formatCompanies={formatCompanies}
                  />
                )}
              >
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          # of Matches
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Companies
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Eligibility
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Email
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredStudents.map((studentData, index) => {
                        const { student_info, matching_organizations, match_count } = studentData;
                        
                        return (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {student_info.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                {match_count}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-xs">
                              <div className="truncate" title={formatCompanies(matching_organizations)}>
                                {formatCompanies(matching_organizations)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                student_info.verification_status === 'verified'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              }`}>
                                {student_info.verification_status === 'verified' ? 'Verified' : 'Pending'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {student_info.eligibility_status !== null ? (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  student_info.eligibility_status === true
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}>
                                  {student_info.eligibility_status === true ? 'Eligible' : 'Not Eligible'}
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 italic">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {student_info.email}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </ResponsiveTable>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 dark:text-gray-500 text-4xl sm:text-6xl mb-4">🏢</div>
                <p className="text-gray-600 dark:text-gray-400 text-base sm:text-lg font-medium">
                  {searchTerm || statusFilter || eligibilityFilter 
                    ? 'No students found matching your criteria'
                    : 'No students found with multiple company matches'
                  }
                </p>
                <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
                  {searchTerm || statusFilter || eligibilityFilter 
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Students with 2 or more organization matches will appear here.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMultiCompanyStudents;